import { QueryKeys } from 'types/dev';
import { ReferenceData } from 'types/reference-data';
import { referenceDataSchema } from 'schemas/reference-data';
import { sortInitialArticulations } from 'utils/articulation';
import { sortInitialPrecedents } from 'utils/precedent';
import { sortInitialSourceInsitutions } from 'utils/source-institution';
import useInstitutionCode from './use-institution-code';
import { useQuery } from '@tanstack/react-query';
import useURLs from 'hooks/use-url';

export type AdvanceErrorTemp<T> = {
  ID: string;
  message: string;
  data: T;
  type: unknown;
};

type AdvanceRequestResponse<T, K = unknown> = {
  awsRequestID: string;
  data: T;
  error: AdvanceErrorTemp<K>;
  functionName: string;
};

async function getReferenceData(
  url: string | undefined,
  institutionCode?: string,
  signal?: AbortSignal | undefined,
): Promise<ReferenceData> {
  if (url === undefined || institutionCode === undefined) {
    throw new Error('Invalid URL');
  }
  const result = await fetch(url, { signal });
  const data = (await result.json()) as AdvanceRequestResponse<ReferenceData>;
  const parse = referenceDataSchema.safeParse(data.data);
  if (!parse.success) {
    console.error('Failed to parse reference data');
    console.error(JSON.stringify(parse.error));
    return {
      articulations: new Map(),
      formSchema: {
        EMPLOYMENT: {
          matchingCriteria: [],
          headings: [],
          subHeadings: [],
          schema: {
            type: 'string',
            properties: {},
            required: [],
          },
        },
        PROFESSIONAL: {
          matchingCriteria: [],
          headings: [],
          subHeadings: [],
          schema: {
            type: 'string',
            properties: {},
            required: [],
          },
        },
        SUBJECT: {
          matchingCriteria: [],
          headings: [],
          subHeadings: [],
          schema: {
            type: 'string',
            properties: {},
            required: [],
          },
        },
        TERTIARY: {
          matchingCriteria: [],
          headings: [],
          subHeadings: [],
          schema: {
            type: 'string',
            properties: {},
            required: [],
          },
        },
      },
      metadata: {
        previousRecords: {
          articulations: 0,
          precedents: 0,
          sourceInstitutions: 0,
        },
        previousTimestamp: 0,
        records: {
          articulations: 0,
          precedents: 0,
          sourceInstitutions: 0,
        },
        timestamp: 0,
      },
      precedents: new Map(),
      sourceInstitutions: new Map(),
    };
  }
  const referenceData = parse.data;
  const articulations = new Map(
    sortInitialArticulations(referenceData.articulations, referenceData.formSchema).map((articulation) => [
      articulation.ID,
      articulation,
    ]),
  );
  const precedents = new Map(
    sortInitialPrecedents(referenceData.precedents, referenceData.formSchema).map((precedent) => [
      precedent.ID,
      precedent,
    ]),
  );
  const sourceInstitutions = new Map(
    sortInitialSourceInsitutions(referenceData.sourceInstitutions).map((sourceInstitution) => [
      sourceInstitution.ID,
      sourceInstitution,
    ]),
  );
  return {
    articulations,
    formSchema: referenceData.formSchema,
    metadata: referenceData.metadata,
    precedents,
    sourceInstitutions,
  };
}

export default function useReferenceData(): ReferenceData | undefined {
  const urls = useURLs();
  const data = useInstitutionCode();
  const { data: referenceData } = useQuery(
    [QueryKeys.REFERENCE_DATA],
    ({ signal }) => getReferenceData(`${urls?.endpoint.url}/reference-data`, data?.institutionCode, signal),
    { enabled: !!urls && !!data?.institutionCode },
  );
  return referenceData;
}
